import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, Camera, Loader2, ImageOff, HandHeart, Video } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import EditableText from '../components/EditableText';
import LinkifiedText from '../components/LinkifiedText';
import TileSection from '../components/TileSection';
import { toEmbedUrl } from '../lib/embedUrl';

/**
 * Detail page for a single ministry / program. Layout mirrors
 * holynameparish.ca/altarservers:
 *   1) slim colored band with the ministry name
 *   2) two-column body — text on the left, photo or video on the right
 *   3) admin-editable News & Events tiles below (kept from previous design)
 */
const MinistryDetail = () => {
  const { slug } = useParams();
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [ministry, setMinistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.listMinistries();
        if (!alive) return;
        setMinistry(data.find(m => m.slug === slug) || null);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [slug]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast({ title: 'Please choose a JPEG, PNG, or WebP image', variant: 'destructive' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Image is larger than 10 MB', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.uploadMinistryImage(ministry.id, fd);
      setMinistry(data);
      toast({ title: 'Photo uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    setUploading(true);
    try {
      const { data } = await api.deleteMinistryImage(ministry.id);
      setMinistry(data);
      toast({ title: 'Photo removed' });
    } catch (e) {
      toast({ title: 'Remove failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <p className="max-w-4xl mx-auto px-6 py-16 text-center text-[#78716c]">Loading…</p>;
  }

  if (!ministry) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-center">
        <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-3">Not found</p>
        <h1 className="font-display text-4xl text-[#2a1a45] mb-4">Ministry not found</h1>
        <p className="text-[#5a534a] mb-8">The ministry you're looking for may have been renamed or removed.</p>
        <Link to="/parish-life/ministries" className="inline-flex items-center gap-1 text-[#3a2359] hover:underline">
          <ChevronLeft size={16} /> Back to Ministries & Programs
        </Link>
      </div>
    );
  }

  const imgSrc = api.ministryImageAbs(ministry.image_url);
  const embedUrl = toEmbedUrl(ministry.video_url);
  const hasMedia = !!(imgSrc || embedUrl);

  return (
    <>
      {/* 1) Slim color band header */}
      <section
        className="bg-[#3a2359] py-8 md:py-10 border-b border-[#2a1a45]"
        data-testid="ministry-detail-band"
      >
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="font-display text-3xl md:text-4xl text-white leading-tight">{ministry.name}</h1>
        </div>
      </section>

      {/* 2) Two-column body */}
      <section className="max-w-5xl mx-auto px-6 pt-10 pb-14">
        <Link
          to="/parish-life/ministries"
          className="inline-flex items-center gap-1 text-sm text-[#3a2359] hover:underline mb-8"
          data-testid="ministry-detail-back"
        >
          <ChevronLeft size={16} /> Back to Ministries &amp; Programs
        </Link>

        <div className={`grid gap-10 md:gap-12 ${hasMedia || isAdmin ? 'md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]' : ''}`}>
          {/* Left column — text */}
          <div className="min-w-0">
            <h2 className="font-display text-2xl md:text-3xl text-[#2a1a45] mb-4">About {ministry.name}</h2>
            <p className="text-lg text-[#3f3a34] leading-relaxed mb-6 font-light">
              <LinkifiedText text={ministry.description} />
            </p>
            <div className="prose max-w-none">
              <EditableText
                contentKey={`ministry_${ministry.slug}_body`}
                label={`${ministry.name} — Detail Body`}
                rows={14}
                defaultValue={`More details about ${ministry.name} coming soon. Please contact the parish office to learn more.`}
                paragraphClass="text-[15px] md:text-base text-[#3f3a34] leading-[1.85]"
              />
            </div>
          </div>

          {/* Right column — media (photo, video, or admin placeholder) */}
          {(hasMedia || isAdmin) && (
            <aside className="md:sticky md:top-32 self-start space-y-4" data-testid="ministry-detail-media">
              {/* Video takes priority when both are set */}
              {embedUrl ? (
                <div className="relative aspect-video overflow-hidden bg-black">
                  <iframe
                    src={embedUrl}
                    title={`${ministry.name} video`}
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              ) : imgSrc ? (
                <div className="relative overflow-hidden">
                  <img
                    src={imgSrc}
                    alt={ministry.name}
                    className="w-full h-auto max-h-[520px] object-cover"
                  />
                </div>
              ) : (
                isAdmin && (
                  <div className="w-full aspect-[4/3] flex flex-col items-center justify-center gap-2 bg-[#faf7f0] border border-dashed border-[#c8b785] text-[#a68a5b] text-xs">
                    <HandHeart size={28} strokeWidth={1.4} />
                    <p>No photo or video yet</p>
                  </div>
                )
              )}

              {isAdmin && (
                <>
                  <input
                    ref={inputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    data-testid="ministry-detail-file-input"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => inputRef.current?.click()}
                      disabled={uploading}
                      data-testid="ministry-detail-change-photo"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#3a2359] text-[#3a2359] bg-white hover:bg-[#3a2359] hover:text-white text-xs font-medium disabled:opacity-70"
                    >
                      {uploading ? <><Loader2 size={12} className="animate-spin" /> Uploading…</> : <><Camera size={12} /> {imgSrc ? 'Change' : 'Upload'} photo</>}
                    </button>
                    {imgSrc && (
                      <button
                        type="button"
                        onClick={handleRemove}
                        disabled={uploading}
                        data-testid="ministry-detail-remove-photo"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#8a2b1a] text-[#8a2b1a] bg-white hover:bg-[#8a2b1a] hover:text-white text-xs font-medium disabled:opacity-70"
                      >
                        <ImageOff size={12} /> Remove photo
                      </button>
                    )}
                  </div>
                  {(ministry.video_url && !embedUrl) && (
                    <p className="text-xs text-[#8a2b1a] flex items-start gap-1.5">
                      <Video size={12} className="mt-0.5 flex-shrink-0" />
                      Video URL is set but not a supported YouTube/Vimeo link — edit this ministry to update.
                    </p>
                  )}
                  <p className="text-[11px] text-[#a68a5b] tracking-wide">
                    Video URL and photo are managed from the Ministries &amp; Programs admin page.
                  </p>
                </>
              )}
            </aside>
          )}
        </div>
      </section>

      {/* 3) News & Events tiles — admin-editable per ministry; hidden on
             public visitors when empty. Kept per user request. */}
      <section className="bg-[#f2ead6]/40 py-14">
        <div className="max-w-5xl mx-auto px-6">
          <TileSection
            sectionKey={`ministry_${ministry.slug}_news_events`}
            title="News & Events"
            addLabel="Add News or Event"
          />
        </div>
      </section>

      {/* Contact CTA */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="mt-6 bg-[#f2ead6]/60 border border-[#e9e0cc] p-8 text-center">
          <h3 className="font-display text-2xl text-[#2a1a45] mb-2">Want to join or learn more?</h3>
          <p className="text-sm text-[#5a534a] mb-5">
            Reach out to the parish office and we'll connect you with the ministry lead.
          </p>
          <Link to="/contact" className="inline-block px-5 py-2.5 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75]">
            Contact the Parish Office
          </Link>
        </div>
      </section>
    </>
  );
};

export default MinistryDetail;
